import React, { useState, useEffect, useCallback } from 'react';
import TenantList from '../components/Tenants/TenantList';
import TenantsTable from '../components/Tenants/TenantsTable';
import { Tenant, tenantService } from '../services/tenantService';
import { useModal } from '../contexts/ModalContext';
import './Tenants.css';

const Tenants: React.FC = () => {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'table' | 'cards'>('table');
  const { showModal } = useModal();

  const loadTenants = useCallback(async () => {
    try {
      setLoading(true);
      const data = await tenantService.getAllTenants();
      setTenants(data);
    } catch (err: any) {
      showModal({
        title: 'Erreur de chargement',
        message: err.response?.data?.message || 'Erreur lors du chargement des tenants',
        type: 'error',
        confirmText: 'OK'
      });
    } finally {
      setLoading(false);
    }
  }, [showModal]);

  useEffect(() => {
    loadTenants();
  }, [loadTenants]);

  const handleUpdateTenant = async (id: number, data: any) => {
    try {
      await tenantService.updateTenant(id, data);
      showModal({
        title: 'Tenant mis à jour',
        message: `Le tenant "${data.nomTenant}" a été mis à jour avec succès.`,
        type: 'success',
        confirmText: 'OK'
      });
      await loadTenants();
    } catch (err: any) {
      showModal({
        title: 'Erreur de mise à jour',
        message: err.response?.data?.message || 'Erreur lors de la mise à jour du tenant',
        type: 'error',
        confirmText: 'OK'
      });
      throw err;
    }
  };

  const handleToggleStatus = async (id: number) => {
    try {
      await tenantService.toggleTenantStatus(id);
      await loadTenants();
    } catch (err: any) {
      showModal({
        title: 'Erreur de changement de statut',
        message: err.response?.data?.message || 'Erreur lors du changement de statut',
        type: 'error',
        confirmText: 'OK'
      });
      throw err;
    }
  };

  const handleRegenerateApiKey = async (id: number) => {
    try {
      await tenantService.regenerateApiKey(id);
      showModal({
        title: 'Clé API régénérée',
        message: 'La clé API a été régénérée avec succès.',
        type: 'success',
        confirmText: 'OK'
      });
      await loadTenants();
    } catch (err: any) {
      showModal({
        title: 'Erreur de régénération',
        message: err.response?.data?.message || 'Erreur lors de la régénération de la clé API',
        type: 'error',
        confirmText: 'OK'
      });
      throw err;
    }
  };

  const handleDeleteTenant = async (id: number) => {
    try {
      await tenantService.deleteTenant(id);
      showModal({
        title: 'Tenant supprimé',
        message: 'Le tenant a été supprimé avec succès.',
        type: 'success',
        confirmText: 'OK'
      });
      await loadTenants();
    } catch (err: any) {
      showModal({
        title: 'Erreur de suppression',
        message: err.response?.data?.message || 'Erreur lors de la suppression du tenant',
        type: 'error',
        confirmText: 'OK'
      });
      throw err;
    }
  };

  return (
    <div className="tenants-page">
      <div className="tenants-header">
        <h1>Gestion des Tenants</h1>
        <div className="header-actions">
          <div className="view-mode-toggle">
            <button
              className={`btn ${viewMode === 'table' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setViewMode('table')}
              title="Vue tableau"
            >
              📊
            </button>
            <button
              className={`btn ${viewMode === 'cards' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setViewMode('cards')}
              title="Vue cartes"
            >
              🃏
            </button>
          </div>
        </div>
      </div>

      {viewMode === 'table' ? (
        <TenantsTable
          tenants={tenants}
          onUpdateTenant={handleUpdateTenant}
          onDeleteTenant={handleDeleteTenant}
          onToggleStatus={handleToggleStatus}
          onRegenerateApiKey={handleRegenerateApiKey}
          loading={loading}
        />
      ) : (
        <TenantList />
      )}
    </div>
  );
};

export default Tenants;


